import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser-native Speech-to-Text hook — no API key, no backend.
 *
 * Uses the W3C SpeechRecognition API (webkitSpeechRecognition in Chrome,
 * Safari, Edge). Falls back gracefully on Firefox by reporting
 * supported: false so the UI can hide the mic button.
 *
 * Returns:
 *   supported  — true if the browser exposes SpeechRecognition
 *   listening  — true while the mic is open
 *   transcript — partial + finalized text from current session
 *   start()    — begin listening
 *   stop()     — finalize current utterance
 *   reset()    — clear transcript
 */
export default function useSpeechRecognition({ lang = 'en-US', continuous = false, interim = true } = {}) {
  const SpeechRecognition =
    typeof window !== 'undefined'
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
  const supported = Boolean(SpeechRecognition);

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recRef = useRef(null);

  useEffect(() => {
    if (!supported) return undefined;
    const rec = new SpeechRecognition();
    rec.lang = lang;
    rec.continuous = continuous;
    rec.interimResults = interim;
    rec.onresult = (event) => {
      let finalT = '';
      let interimT = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        if (r.isFinal) finalT += r[0].transcript;
        else interimT += r[0].transcript;
      }
      setTranscript((prev) => (finalT ? (prev + finalT).trim() : `${prev}${interimT}`.trim()));
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    return () => {
      try { rec.abort(); } catch { /* ignore */ }
      recRef.current = null;
    };
  }, [SpeechRecognition, supported, lang, continuous, interim]);

  const start = useCallback(() => {
    if (!supported || listening || !recRef.current) return;
    setTranscript('');
    try {
      recRef.current.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, [supported, listening]);

  const stop = useCallback(() => {
    if (!supported || !recRef.current) return;
    try { recRef.current.stop(); } catch { /* ignore */ }
    setListening(false);
  }, [supported]);

  const reset = useCallback(() => setTranscript(''), []);

  return { supported, listening, transcript, start, stop, reset };
}
